import { useState, useEffect } from 'react';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#services', label: 'Services' },
    { href: '#why-us', label: 'Why Us' },
    { href: '#gallery', label: 'Gallery' },
    { href: '#testimonials', label: 'Testimonials' },
    { href: '#contact', label: 'Contact' },
  ];

  return (
    <section className="relative min-h-screen flex items-center">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || menuOpen ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img src="https://public.readdy.ai/ai/img_res/1609b5eb-0391-4869-9c8e-411d7ca8bbc2.png" alt="Logo" className={`h-10 w-auto transition-all ${scrolled || menuOpen ? '' : 'brightness-0 invert'}`} />
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
            {navLinks.map(link => (
              <a key={link.href} href={link.href} className={`font-medium tracking-wide transition-colors text-sm lg:text-base ${scrolled ? 'text-gray-700 hover:text-[#1e40af]' : 'text-white hover:text-white/80'}`}>
                {link.label}
              </a>
            ))}
            <a href="tel:555-0123" className={`flex items-center font-semibold text-sm lg:text-base whitespace-nowrap ${scrolled ? 'text-[#1e40af]' : 'text-white'}`}>
              <i className="ri-phone-line mr-1"></i>(555) 123-4567
            </a>
            <a href="#contact" className="bg-[#1e40af] text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-[#1e3a8a] transition-colors whitespace-nowrap cursor-pointer text-sm lg:text-base">
              Request Quote
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${scrolled || menuOpen ? 'text-gray-700' : 'text-white'}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-1">
            {navLinks.map(link => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="block px-4 py-3 text-gray-700 font-medium rounded-lg hover:bg-gray-50 hover:text-[#1e40af] transition-colors cursor-pointer"
              >
                {link.label}
              </a>
            ))}
            <a href="tel:555-0123" className="flex items-center px-4 py-3 text-[#1e40af] font-semibold rounded-lg hover:bg-gray-50 cursor-pointer">
              <i className="ri-phone-line mr-2"></i>(555) 123-4567
            </a>
            <a
              href="#contact"
              onClick={() => setMenuOpen(false)}
              className="block w-full text-center bg-[#1e40af] text-white px-4 py-3 rounded-lg font-semibold hover:bg-[#1e3a8a] transition-colors cursor-pointer mt-2"
            >
              Request Free Quote
            </a>
          </div>
        )}
      </nav>

      {/* Hero Content */}
      <div className="w-full flex flex-col md:flex-row min-h-screen">
        {/* Left Content */}
        <div className="w-full md:w-1/2 flex items-center px-6 sm:px-10 md:px-16 lg:px-24 pt-28 pb-16 md:py-0 bg-white order-2 md:order-1">
          <div className="max-w-xl w-full">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-[#2C2C2C] leading-tight mb-6 md:mb-8">
              Professional Home Services You Can Trust
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl text-[#6B6B6B] mb-8 md:mb-12 leading-relaxed">
              Reliable service, quality workmanship, and fast response times for homeowners in the local area.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a href="#contact" className="bg-[#1e40af] text-white px-6 py-4 rounded-lg font-bold text-base lg:text-lg hover:bg-[#1e3a8a] transition-all flex items-center justify-center whitespace-nowrap cursor-pointer">
                Request a Free Quote
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
              <a href="tel:555-0123" className="border-2 border-[#1e40af] text-[#1e40af] px-6 py-4 rounded-lg font-bold text-base lg:text-lg hover:bg-[#1e40af] hover:text-white transition-all flex items-center justify-center whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line mr-2"></i>Call Now
              </a>
            </div>
          </div>
        </div>

        {/* Right Visual */}
        <div className="w-full md:w-1/2 relative h-72 sm:h-96 md:min-h-screen order-1 md:order-2">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20contractor%20working%20on%20home%20improvement%20project%20with%20tools%20and%20equipment%20in%20modern%20residential%20setting%2C%20clean%20bright%20natural%20lighting%20showing%20skilled%20craftsmanship%20and%20attention%20to%20detail%2C%20worker%20wearing%20safety%20gear%20and%20professional%20uniform%20demonstrating%20expertise%20and%20reliability&width=1200&height=1600&seq=hero-contractor-001&orientation=portrait"
            alt="Professional contractor at work"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-black/20 to-black/30"></div>
        </div>
      </div>
    </section>
  );
}
